import React from 'react'
import { Head, ModuleContainer, ProfileCard, WelcomeBackRecognition } from '../../../src/component'
import { TrackPageViewed } from '../../../src/integrations/monitoring'
import { CheckoutPage, Features } from '../../../src/model'
import {
  useCachedFeatureFlag,
  useCheckoutState,
  useShouldShowWelcomeBackRecognition,
  useShowLoaderOnly,
  useTriggerOneOfferPageExperimentExposure,
  useTriggerRememberMeExperimentExposure,
} from '../../../src/state'
import { DesignLanguage, Heading, Loader, useIsDesignLanguage } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { useTranslation } from '../../utils/convergence'
import { isAfterpayInAppBrowser } from '../../utils/device'
import { useMintPersistentSessionOnLogin } from '../../flow/remember-me/hooks'
import { getLoggedInLoadingMessages } from './helper'
import {
  usePaymentMethodRouting,
  useDispatchAUPayMonthlyOrderLimitTerminalError,
  usePBIRejectionRedirect,
  usePostLoginNavigation,
  usePrefetchPostLoginRoutes,
  useStartCheckoutOnceReady,
  useSyncApplePayFlagsToStore,
  useTrackOneOfferPageRouteResolved,
} from './hooks'

export const LoggedInPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const { loggedInLoadingMessageKey } = useCheckoutState()
  useMintPersistentSessionOnLogin()
  useTrackOneOfferPageRouteResolved()
  useTriggerOneOfferPageExperimentExposure()
  useTriggerRememberMeExperimentExposure()
  useCachedFeatureFlag(Features.CHECKOUT_MANUAL_PROMO_CODE_ENTRY, false)

  const { navigatePostLogin } = usePostLoginNavigation()
  useSyncApplePayFlagsToStore()
  const requestCompleted = useStartCheckoutOnceReady()
  useDispatchAUPayMonthlyOrderLimitTerminalError()
  usePaymentMethodRouting({ requestCompleted, navigatePostLogin })
  usePrefetchPostLoginRoutes()
  usePBIRejectionRedirect()

  const { headingSize } = useCheckoutSizes()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isAfterpayInAppSession = isAfterpayInAppBrowser()
  const showLoaderOnly = useShowLoaderOnly()
  const shouldShowWelcomeBack = useShouldShowWelcomeBackRecognition()
  const pageTitle = `${t('common:brandName')}`
  const { title: loadingTitle, description: loadingDescription } = getLoggedInLoadingMessages(t, loggedInLoadingMessageKey ?? undefined)

  if (isArcadeDesignLanguage) {
    return (
      <>
        {shouldShowWelcomeBack ? (
          <WelcomeBackRecognition />
        ) : (
          <Loader alignCenter withHeader text={loggedInLoadingMessageKey ? loadingDescription : undefined} />
        )}
        <TrackPageViewed />
      </>
    )
  }

  return (
    <>
      <Head title={pageTitle} />
      {shouldShowWelcomeBack ? (
        <WelcomeBackRecognition />
      ) : isAfterpayInAppSession ? (
        <Loader alignCenter withHeader={!showLoaderOnly} />
      ) : (
        <>
          {loggedInLoadingMessageKey && <Heading size={headingSize}>{loadingTitle}</Heading>}
          <ModuleContainer padding={{ vertical: 'Large', horizontal: 'XLarge' }}>
            <ProfileCard />
            <Loader text={loadingDescription} />
          </ModuleContainer>
        </>
      )}
      <TrackPageViewed eventProperties={{ loggedInLoadingMessageKey }} />
    </>
  )
}
